import { Paper, Stack, styled, Typography, useTheme } from '@mui/material';

import AlertExclamationCircleIcon from 'modules/common/components/Icons/AlertExclamationCircleIcon';
import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import CircleOutlinedIcon from 'modules/common/components/Icons/CircleOutlinedIcon';

/**
 * How the row reads.
 *
 * One prop drives the badge and the subtitle colour together, so a caller cannot pair a green
 * check with a red reason. Deliberately a single axis: these rows are never tinted, so the
 * badge is the only thing reporting an outcome and a second prop could only contradict it.
 */
type StatusRowTone = 'neutral' | 'info' | 'success' | 'error';

/**
 * The filled check, in green.
 *
 * Its own component because the colour has to come through `fill`, and that needs the theme.
 * `color="success"` does nothing here: `CheckCircleFilledIcon` writes an explicit `fill`
 * attribute on both paths, defaulting the disc to `primary.main`, and an attribute beats the
 * `currentColor` that MUI's `color` prop drives. So the matched rows rendered a purple tick.
 * The other three badges have no `fill` of their own, which is why `color` works for them.
 */
function SuccessBadge() {
    const theme = useTheme();
    return <CheckCircleFilledIcon fill={theme.palette.success.main} data-testid="status-row-badge-success" />;
}

/** The glyph and its palette colour per tone, so a caller passes a word rather than both. */
const BADGES: Record<StatusRowTone, React.ReactNode> = {
    neutral: <CircleOutlinedIcon color="disabled" data-testid="status-row-badge-neutral" />,
    // Accepted but unresolved: it carries a reason, so it needs a glyph, but not a red one.
    info: <AlertExclamationCircleIcon color="disabled" data-testid="status-row-badge-info" />,
    success: <SuccessBadge />,
    error: <AlertExclamationCircleIcon color="error" data-testid="status-row-badge-error" />,
};

/** Only `error` states its reason in the reason's own colour; the rest are secondary prose. */
const SUBTITLE_COLORS: Record<StatusRowTone, string> = {
    neutral: 'text.secondary',
    info: 'text.secondary',
    success: 'text.secondary',
    error: 'error.main',
};

/**
 * A discrete bordered card, unlike `UploadedFileRow`'s joined block.
 *
 * These rows carry a two-line body and sit in gapped sections, so there is no
 * `isFirst`/`isLast` radius protocol to negotiate — which is most of why this is a sibling of
 * that component rather than a variant of it.
 *
 * The border, radius and background all come from `Paper`'s `outlined` variant rather than
 * being restated here, so the card tracks the design system instead of a copy of it. Only the
 * padding and the row's own flex layout are added.
 */
const RowCard = styled(Paper)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
}));

/** Fixed leading slot, so titles align down a section whatever badge each row carries. */
const Badge = styled(Stack)({
    flexShrink: 0,
    '& .MuiSvgIcon-root': { fontSize: 20 },
});

interface StatusRowProps {
    readonly tone: StatusRowTone;
    readonly title: string;
    /** Second line. Omitted rather than blank when there is nothing to say. */
    readonly subtitle?: string;
    /** Further secondary lines under the subtitle, one per fact. Never restate the badge's status. */
    readonly notes?: readonly string[];
    /** Caller-owned trailing controls. Style with `StatusRowIconButton` to match. */
    readonly actions?: React.ReactNode;
}

/**
 * A bordered row stating one thing's status: a badge, a title, an optional reason beneath, and
 * caller-owned controls.
 *
 * Domain-free by construction — it takes strings, not an entity. That is the point: a row can
 * describe something that has no backing record at all, such as a required item nothing has
 * satisfied yet. Contrast `UploadedFileRow`, whose every prop (`name`, `sizeBytes`, `url`,
 * `onDelete`) presumes a file, and which joins into a single block rather than standing alone.
 */
export default function StatusRow({
    tone,
    title,
    subtitle = undefined,
    notes = undefined,
    actions = undefined,
}: Readonly<StatusRowProps>) {
    return (
        <RowCard variant="outlined">
            <Badge>{BADGES[tone]}</Badge>
            <Stack sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="bodyReducedSemiBold" color="text.primary" noWrap>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color={SUBTITLE_COLORS[tone]}>
                        {subtitle}
                    </Typography>
                )}
                {notes?.map((note) => (
                    <Typography key={note} variant="caption" color="text.secondary">
                        {note}
                    </Typography>
                ))}
            </Stack>
            {actions}
        </RowCard>
    );
}
